import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useUserData } from '@/hooks/useUserData';
import Header from '../components/Header';
import ActionButton from '../components/ActionButton';
import ReportButton from '@/components/dashboard/ReportButton';
import OnboardingButton from '@/components/OnboardingButton';
import LoadingScreen from '@/components/common/LoadingScreen';
import { getEvaluatorsByUserId } from '@/services/evaluatorService';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { Navigate } from 'react-router-dom';

export default function UserDashboard() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { userData, loading, error } = useUserData();
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [planName, setPlanName] = useState<string>('loading...');

  useEffect(() => {
    async function loadEvaluators() {
      if (!currentUser) return;
      try {
        const evaluatorsList = await getEvaluatorsByUserId(currentUser.uid);
        setEvaluators(evaluatorsList);
      } catch (error) {
        console.error('Error loading evaluators:', error);
      }
    }
    loadEvaluators();
  }, [currentUser]);

  useEffect(() => {
    async function loadPlanName() {
      if (!userData?.selectedPlanId) return;
      
      try {
        const planRef = doc(db, collections.plans, userData.selectedPlanId);
        const planDoc = await getDoc(planRef);
        
        if (planDoc.exists()) {
          setPlanName(planDoc.data().name);
        }
      } catch (error) {
        console.error('Error loading plan name:', error);
        setPlanName('Unknown Plan');
      }
    }
    
    loadPlanName();
  }, [userData?.selectedPlanId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error: any) {
      console.error('Failed to log out:', error);
    }
  };

  // Redirect to sign in if not authenticated
  if (!currentUser && !loading) {
    return <Navigate to="/signin" />;
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg max-w-md text-center">
          <p className="mb-4">{error || 'Failed to load user data'}</p>
          <button
            onClick={handleLogout}
            className="text-primary hover:text-primary-hover underline"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  const completedEvaluations = evaluators.filter(e => e.evaluationCompleted).length;
  const totalEvaluators = userData.evaluatorsCount;
  const pendingContextCount = evaluators.filter(e => !e.contextualized).length;
  const contextDescription = pendingContextCount > 0 
    ? `${pendingContextCount} evaluator${pendingContextCount === 1 ? '' : 's'} pending context` 
    : 'All contexts provided';

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen bg-gray-50"
    >
      <Header 
        onLogout={handleLogout}
        onProfileClick={() => navigate('/profile')}
        userData={userData}
      />
      
      <main className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center sm:text-left">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Welcome{userData?.firstName ? `, ${userData.firstName}` : ''}! Your plan is{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-primary via-secondary to-primary bg-clip-text text-transparent animate-gradient">
                  {planName}
                </span>
                <span className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 blur-sm rounded-lg -z-10"></span>
              </span>
            </h1>
            <p className="text-gray-600 text-lg mb-4">
              Track here every step of your evaluation process seamlessly. Assess yourself, nominate evaluators, monitor their responses, and access your comprehensive report as soon as the evaluations are complete.
            </p>
          </div>

          <div className="space-y-6">
            {/* Onboarding Button */}
            <div className="w-full">
              <OnboardingButton isCompleted={userData.onboardingCompleted} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ActionButton
                title="Nominate and Add Context for Evaluators"
                description={`${completedEvaluations} of ${totalEvaluators} evaluators have provided feedback`}
                secondaryDescription={contextDescription}
                allEvaluationsCompleted={completedEvaluations}
                currentEvaluators={totalEvaluators}
                isCompleted={false}
                isSuccess={userData.evaluatorsFinalized}
                route="/nominate-evaluators"
                disabled={userData.evaluatorsFinalized}
                onboardingRequired={!userData.onboardingCompleted}
              />

              <ActionButton
                title="Complete your DISC Assessment"
                description="Understand your behavioral style and preferences"
                isCompleted={userData.discAssessmentCompleted}
                route="/disc-assessment"
                onboardingRequired={!userData.onboardingCompleted}
              />

              <ActionButton
                title="Complete Self-Assessment"
                description="Reflect on your skills and areas for improvement"
                isCompleted={userData.selfAssessmentCompleted}
                route="/self-assessment"
                onboardingRequired={!userData.onboardingCompleted}
              />
            </div>

            <div className="w-full">
              <ReportButton 
                userData={{
                  ...userData,
                  // Override reportAvailable if onboarding is not completed
                  reportAvailable: userData.reportAvailable && userData.onboardingCompleted
                }} 
              />
            </div>
          </div>
        </div>
      </main>
    </motion.div>
  );
}